import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeRole } from "@/components/RoleBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Calculator, Plus, Trash2, TriangleAlert } from "lucide-react";
import { fmtMoney as fmt } from "@/lib/format";

export const PAX_COMPONENT_OPTIONS = [
  { key: "fondsgebundene_lebensversicherung", name: "Fondsgebundene Lebensversicherung", max: 40 },
  { key: "ausbaugarantie", name: "Ausbaugarantie", max: 30 },
  { key: "erwerbsunfaehigkeitsrente", name: "Erwerbsunfähigkeitsrente", max: 35 },
  { key: "praemienbefreiung", name: "Prämienbefreiung bei EU", max: 35 },
];

/** PAX FondsStar/LifeStar — contract components editor + live commission preview.
 *  Rendered inside UploadPolicyDialog when company is PAX (vita_3a / vita_3b).
 *  Data lives in policy.details: { pax_product, pax_components: [{key, name, premium_monthly, duration_years}] }
 */
export default function PaxComponentsSection({ company, details, setDetails }) {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const canSeeCommission = ["master", "control", "finanza"].includes(role);
  const isPax = /\bpax\b/i.test(company || "");
  const comps = Array.isArray(details?.pax_components) ? details.pax_components : [];
  const product = details?.pax_product || "";
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!isPax || comps.length === 0 || !canSeeCommission) { setPreview(null); return; }
    const tmo = setTimeout(async () => {
      try {
        const r = await api.post("/commission-engine/preview", {
          company: "PAX",
          product_name: product || "",
          components: comps.map((c) => ({
            key: c.key || "",
            premium_monthly: parseFloat(c.premium_monthly) || 0,
            duration_years: parseInt(c.duration_years) || 0,
          })),
        });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 600);
    return () => clearTimeout(tmo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(comps), product, isPax]);

  if (!isPax) return null;

  const patchDetails = (patch) => setDetails({ ...(details || {}), ...patch });
  const updateComp = (idx, patch) =>
    patchDetails({ pax_components: comps.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const addComp = () => {
    const used = new Set(comps.map((c) => c.key));
    const next = PAX_COMPONENT_OPTIONS.find((o) => !used.has(o.key)) || PAX_COMPONENT_OPTIONS[0];
    patchDetails({ pax_components: [...comps, { key: next.key, name: next.name, premium_monthly: 0, duration_years: 0 }] });
  };
  const removeComp = (idx) => patchDetails({ pax_components: comps.filter((_, i) => i !== idx) });

  const previewRow = (key) => (preview?.rows || []).find((r) => r.key === key);

  return (
    <div className="border border-amber-300/60 dark:border-amber-700/40 rounded-md overflow-hidden" data-testid="pax-components-section">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50/60 dark:bg-amber-900/10 border-b border-amber-300/40">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-amber-700" strokeWidth={1.8} />
          <span className="text-xs font-bold uppercase tracking-widest text-amber-800 dark:text-amber-400">
            PAX Vertrags-Komponenten
          </span>
          <span className="text-[10px] text-muted-foreground hidden sm:inline">(Vertragsübersicht Seiten 1, 9–10)</span>
        </div>
        <div className="w-40">
          <Select value={product} onValueChange={(v) => patchDetails({ pax_product: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="pax-product-select"><SelectValue placeholder="Produkt…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="FondsStar">PAX FondsStar</SelectItem>
              <SelectItem value="LifeStar">PAX LifeStar</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {comps.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-2">
            Keine Komponenten — per AI-Extraktion automatisch befüllt oder manuell hinzufügen.
          </div>
        )}
        {comps.map((c, i) => {
          const opt = PAX_COMPONENT_OPTIONS.find((o) => o.key === c.key);
          const dur = parseInt(c.duration_years) || 0;
          const capped = opt && dur > opt.max;
          const pr = previewRow(c.key);
          return (
            <div key={i} className="grid grid-cols-[1.6fr_0.9fr_0.9fr_auto] gap-2 items-end" data-testid={`pax-comp-row-${i}`}>
              <div>
                {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Komponente</Label>}
                <Select value={c.key || ""} onValueChange={(v) => {
                  const o = PAX_COMPONENT_OPTIONS.find((x) => x.key === v);
                  updateComp(i, { key: v, name: o?.name || v });
                }}>
                  <SelectTrigger className="h-9 text-xs mt-1" data-testid={`pax-comp-key-${i}`}><SelectValue placeholder="Wählen…" /></SelectTrigger>
                  <SelectContent>
                    {PAX_COMPONENT_OPTIONS.map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.name} (max {o.max} J.)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Prämie/Monat</Label>}
                <Input type="number" step="0.05" value={c.premium_monthly ?? ""} placeholder="CHF"
                  onChange={(e) => updateComp(i, { premium_monthly: e.target.value })}
                  className="h-9 mt-1 text-xs" data-testid={`pax-comp-premium-${i}`} />
              </div>
              <div>
                {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Dauer (Jahre)</Label>}
                <div className="relative">
                  <Input type="number" value={c.duration_years ?? ""} placeholder="Jahre"
                    onChange={(e) => updateComp(i, { duration_years: e.target.value })}
                    className={`h-9 mt-1 text-xs ${capped ? "border-amber-500 pr-7" : ""}`} data-testid={`pax-comp-years-${i}`} />
                  {capped && (
                    <span title={`Über Maximum — Berechnung auf ${opt.max} Jahre begrenzt`}>
                      <TriangleAlert className="w-3.5 h-3.5 text-amber-600 absolute right-2 top-3.5" />
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 pb-0.5">
                {canSeeCommission && pr && (
                  <span className="text-[11px] font-mono font-bold text-amber-700 dark:text-amber-400 whitespace-nowrap" data-testid={`pax-comp-amount-${i}`}>
                    {fmt(pr.amount_chf)}
                  </span>
                )}
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50"
                  onClick={() => removeComp(i)} data-testid={`pax-comp-del-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}

        <div className="flex items-center justify-between pt-1">
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={addComp} data-testid="pax-add-component">
            <Plus className="w-3.5 h-3.5" /> Komponente
          </Button>
          {canSeeCommission && preview && (
            <div className="text-right" data-testid="pax-commission-total">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Provision ({preview.product_name} · Rate {preview.rate})
              </div>
              <div className="font-display font-extrabold text-lg text-amber-700 dark:text-amber-400">
                CHF {fmt(preview.total_chf)}
              </div>
            </div>
          )}
        </div>
        {canSeeCommission && comps.some((c) => {
          const o = PAX_COMPONENT_OPTIONS.find((x) => x.key === c.key);
          return o && (parseInt(c.duration_years) || 0) > o.max;
        }) && (
          <div className="text-[10px] text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
            <TriangleAlert className="w-3 h-3 shrink-0" />
            Vertragsdauer über dem PAX-Maximum — die Provision wird automatisch auf das Maximum begrenzt (MIN-Regel).
          </div>
        )}
      </div>
    </div>
  );
}
